import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate, useLocation } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { ref, get } from 'firebase/database';
import { auth, database } from './config/firebase';
import { PLATFORM_HOSTNAMES } from './config/platformHostnames';
import { CompanyProvider, useCompany } from './contexts/CompanyContext';
import { useCompanyBySlug } from './hooks/useCompanyBySlug';

// ── Pagine pubbliche / SEO-indicizzate ──────────────────────────────────────
// Import statico (nel bundle principale): sono le pagine che Google deve
// poter indicizzare, incluse quelle raggiunte dal routing multi-tenant per
// slug azienda (PublicCompanySite, più sotto). NON vanno mai messe in lazy():
// aggiungere un ulteriore round-trip di rete prima che il contenuto testuale
// compaia è un rischio inutile su pagine pensate per essere trovate da Google.
import PublicCarList from './pages/PublicCarList';
import ContactPage from './pages/ContactPage';
import SearchCompanies from './pages/SearchCompanies';
import PrivacyPage from './pages/PrivacyPage';
import NotFoundPage from './pages/NotFoundPage';

// ── Pagine dell'app gestionale (autenticate) ────────────────────────────────
import { SubscriptionBanner, SubscriptionBlockedScreen, AccountBlockedScreen, StorageQuotaBanner, ImpersonationBanner } from './components/SubscriptionBanner';
import AdminEmailVerificationScreen from './components/AdminEmailVerificationScreen';
import { useTestMode } from './hooks/useTestMode';
import { FlaskConical } from 'lucide-react';
import { logAccess } from './utils/logAccessClient';
import { holdSplash, isSplashDone } from './utils/splash';
import ErrorBoundary from './components/ErrorBoundary';

// ── Pagine marketing ────────────────────────────────────────────────────────
import MarketingLayout    from './marketing/MarketingLayout';
import MarketingHome      from './marketing/pages/Home';
import MarketingFunz      from './marketing/pages/Funzionalita';
import MarketingCome      from './marketing/pages/ComeFunziona';
import MarketingPrezzi    from './marketing/pages/Prezzi';
import MarketingTutorial  from './marketing/pages/Tutorial';
import MarketingFAQ       from './marketing/pages/FAQ';
import MarketingContatti  from './marketing/pages/Contatti';
import MarketingChiSiamo  from './marketing/pages/ChiSiamo';
import GuideQuando         from './marketing/pages/QuandoServeGestionale';
import GuideCosto          from './marketing/pages/QuantoCostaGestionale';
import GuideScegliere      from './marketing/pages/ComeScegliereGestionale';
import GuideModo           from './marketing/pages/GestionaleNoleggioMoto';
import GuideCargos         from './marketing/pages/GestionaleNoleggioCargos';
import GuideDoppie         from './marketing/pages/ComeEvitareDoppiePrenotazioni';
import GuideChecklist      from './marketing/pages/ChecklistConfrontoGestionali';
import GuideExcelVs        from './marketing/pages/ExcelWhatsappOGestionale';
import { ToastProvider } from './components/ToastProvider';
import { ConfirmProvider } from './components/ConfirmProvider';

// BUG FIX (Settembre 2026, build Windows): queste const erano infilate PRIMA
// degli import di SubscriptionBanner/marketing qui sopra, cioè in mezzo al
// blocco di import — la regola ESLint "import/first" (attiva nella config
// CRA di questo progetto) impedisce di avere import DOPO una qualunque altra
// istruzione, comprese queste const. In locale su alcune macchine/versioni
// di CRA questo resta un warning non bloccante, ma con CI=true (comune su
// pipeline Windows/CI) react-scripts lo tratta come errore e la build fallisce.
// Spostate qui, dopo TUTTI gli import: stesso identico comportamento
// (React.lazy per il code-splitting delle rotte bloccate da robots.txt),
// nessuna modifica funzionale, solo riordino.
//
// Import differito (React.lazy): sono tutte rotte bloccate da robots.txt
// (/login, /register, /admin, /admin/*, /account, /platform-admin — vedi
// public/robots.txt), quindi caricarle in un chunk separato non ha ALCUN
// impatto SEO — Google non le scansiona comunque. In compenso tolgono dal
// bundle principale (scaricato anche da un visitatore del solo sito
// marketing) l'intero gestionale: ManualBookingCreator, ContactsManagement,
// AdminDashboard e le altre pagine più pesanti del progetto.
const AuthScreen         = lazy(() => import('./pages/AuthScreen'));
const RegisterScreen     = lazy(() => import('./pages/RegisterScreen'));
const CustomerAuthScreen = lazy(() => import('./pages/CustomerAuthScreen'));
const AuthBrokerScreen   = lazy(() => import('./pages/AuthBrokerScreen'));
const AdminDashboard     = lazy(() => import('./pages/AdminDashboard'));
const PlatformAdminPanel = lazy(() => import('./pages/PlatformAdminPanel'));
const UserDashboard      = lazy(() => import('./pages/UserDashboard'));
const ContractSignPage   = lazy(() => import('./pages/ContractSignPage'));

/**
 * ARCHITETTURA DI HOSTING:
 *
 * Un solo bundle React (CRA), un solo dominio noleggioincloud.app, tutto sotto
 * "/" (vedi DEPLOY.md — il vecchio piano a due bundle separati, marketing Vite
 * + gestionale CRA, è stato abbandonato: il marketing vive dentro src/marketing/
 * di questa stessa app, sulle stesse route qui sotto).
 */

// Schermata di caricamento condivisa.
// Al caricamento/refresh della pagina mantiene visibile lo splash con il logo
// definito in public/index.html (vedi src/utils/splash.js). Durante la
// navigazione successiva lo splash NON ricompare: mostra solo un piccolo
// spinner discreto.
function LoadingScreen() {
  const [afterSplash] = useState(isSplashDone);
  useEffect(() => holdSplash(), []);
  if (!afterSplash) return null;
  return (
    <div className="min-h-screen flex items-center justify-center" role="status" aria-label="Caricamento in corso">
      <div className="w-10 h-10 border-4 border-slate-200 border-t-blue-600 rounded-full animate-spin"></div>
    </div>
  );
}

// Protegge le rotte /admin/*: richiede utente autenticato E admin di una company
// (o super admin di piattaforma). L'enforcement reale resta nelle Firebase Rules —
// questo componente serve solo a non mostrare l'interfaccia admin a chi non deve vederla.
//
// FASE 8: blocca anche in base allo stato dell'abbonamento — se 'expired' o
// 'cancelled', sostituisce l'intero pannello con la schermata di riattivazione
// (coerente con "blocco totale del pannello" del documento di piano). Il super
// admin di piattaforma non è mai bloccato da questo controllo (deve poter
// accedere per supporto anche ad aziende con abbonamento scaduto).
function RequireAdmin({ user, onLogout, children }) {
  const { isAdmin, loading, subscriptionStatus, accountBlocked, isPlatformAdmin } = useCompany();

  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  // La home "/" è servita dal bundle marketing (bundle separato): usiamo
  // window.location invece di <Navigate> per forzare un reload vero della pagina.
  if (!isAdmin) { window.location.replace('/'); return null; }

  // Se l'amministratore non ha ancora verificato l'email, mostriamo la schermata di blocco
  if (!isPlatformAdmin && !user.emailVerified) {
    return <AdminEmailVerificationScreen user={user} onLogout={onLogout} />;
  }

  // Il banner di impersonazione va montato QUI, prima dei rami sotto, non
  // dentro AdminDashboard: se l'azienda impersonata ha accountBlocked=true o
  // subscriptionStatus scaduto, i due `return` sotto sostituiscono l'intero
  // pannello con AccountBlockedScreen/SubscriptionBlockedScreen — senza
  // questo banner qui sopra, il platform admin resterebbe bloccato su quelle
  // schermate senza alcun modo di uscire dalla sessione di impersonazione.
  if (!isPlatformAdmin && accountBlocked) {
    return (
      <>
        <ImpersonationBanner />
        <AccountBlockedScreen />
      </>
    );
  }

  if (!isPlatformAdmin && (subscriptionStatus === 'expired' || subscriptionStatus === 'cancelled')) {
    return (
      <>
        <ImpersonationBanner />
        <SubscriptionBlockedScreen />
      </>
    );
  }

  return (
    <>
      <ImpersonationBanner />
      <SubscriptionBanner />
      <StorageQuotaBanner />
      {children}
    </>
  );
}

// Unica email autorizzata ad accedere al pannello /platform-admin.
// Controllo VOLUTAMENTE più stretto di isPlatformAdmin: essere in
// platformAdmins/{uid} (usato per Stripe Connect, sezione 7) non basta più
// per questa rotta — deve corrispondere anche questa email esatta.
// Se in futuro più persone dovessero avere accesso al pannello piattaforma,
// qui andrebbe trasformato in un array di email autorizzate.
const PLATFORM_ADMIN_EMAIL = 'noleggio@admin.it';

// Protegge /platform-admin: richiede utente autenticato E platformAdmin E
// email esattamente uguale a PLATFORM_ADMIN_EMAIL. Nessun link o pulsante
// nell'app punta qui: raggiungibile solo digitando l'URL direttamente.
// A differenza di RequireAdmin, qui non c'entra lo stato abbonamento di
// nessuna azienda specifica — è una rotta trasversale a tutte le aziende,
// quindi l'unico controllo di piano è assente. L'enforcement reale resta
// comunque lato server, dentro la Cloud Function getPlatformOverview.
function RequirePlatformAdmin({ user, children }) {
  const { isPlatformAdmin, loading } = useCompany();

  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (!isPlatformAdmin) { window.location.replace('/'); return null; }
  if (user.email !== PLATFORM_ADMIN_EMAIL) { window.location.replace('/'); return null; }

  return children;
}

// Wrapper per /login e /register: NON decide il redirect in base alla sola
// presenza di un utente Firebase Auth (user), ma in base a isAdmin risolto
// da CompanyContext.
//
// PERCHÉ: creare un account (createUserWithEmailAndPassword, dentro
// RegisterScreen Step 1) autentica IMMEDIATAMENTE l'utente lato Firebase Auth,
// anche prima che la company venga effettivamente creata (Step 2, tramite la
// Cloud Function registerCompany). Se il redirect si basasse sul solo `user`,
// l'utente verrebbe rimbalzato via da /register subito dopo lo Step 1 — mai
// arrivando allo Step 2 — perché "user" diventa truthy troppo presto.
// Usando isAdmin invece, il redirect scatta solo quando la company esiste
// DAVVERO (dopo che registerCompany ha completato con successo).
function PublicOnlyRoute({ user, children }) {
  const { isAdmin, loading } = useCompany();

  if (user && loading) return <LoadingScreen />;
  if (user && isAdmin) return <Navigate to="/admin" replace />;

  return children;
}

// Wrapper per il sito pubblico di prenotazione di una company, risolto dallo slug in URL.
// La risoluzione slug -> companyId (tramite useCompanyBySlug, Fase 5) avviene dentro
// PublicCarList stesso — qui passiamo solo lo slug letto dai parametri di rotta.
function PublicCompanySite({ user }) {
  const { companySlug } = useParams();
  return <PublicCarList companySlug={companySlug} user={user} />;
}

// Wrapper per la privacy policy di una specifica azienda (/:companySlug/privacy).
// Risolve lo slug in companyId con lo stesso hook usato da PublicCarList, così
// PrivacyPage mostra i dati reali del Titolare invece dei default vuoti.
function CompanyPrivacyPage({ companySlug: propSlug }) {
  const { companySlug: paramSlug } = useParams();
  const companySlug = propSlug || paramSlug;
  const navigate = useNavigate();
  const { companyId } = useCompanyBySlug(companySlug);
  const isCustomDomain =
    typeof window !== 'undefined' && !PLATFORM_HOSTNAMES.has(window.location.hostname);
  const basePath = isCustomDomain ? '' : `/${companySlug}`;
  return (
    <PrivacyPage
      companyId={companyId}
      basePath={basePath}
      onBack={() => navigate(basePath || '/')}
    />
  );
}

// Domini del sistema — qualsiasi altra hostname viene trattata come dominio personalizzato
// (definizione condivisa in ./config/platformHostnames, usata anche da config/firebase.js
// per decidere dove attivare App Check/reCAPTCHA)

/**
 * CustomDomainApp — se l'utente visita un dominio personalizzato (es. tuonoleggio.it)
 * risolve l'hostname nell'indice customDomainsIndex e mostra direttamente il sito
 * dell'azienda corrispondente (senza slug nell'URL, routing pulito).
 * Se l'hostname è un dominio di piattaforma, renderizza normalmente AppRoutes.
 */
function CustomDomainApp({ children }) {
  const hostname = window.location.hostname;

  // Dominio di piattaforma → comportamento normale
  if (PLATFORM_HOSTNAMES.has(hostname)) {
    return children;
  }

  return <CustomDomainResolver hostname={hostname}>{children}</CustomDomainResolver>;
}

function CustomDomainResolver({ hostname, children }) {
  const [slug, setSlug] = useState(null);    // null = loading, false = not found
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const domainKey = hostname.replace(/\./g, '_');
    get(ref(database, `customDomainsIndex/${domainKey}`))
      .then(snap => {
        if (snap.exists()) {
          setSlug(snap.val());
        } else {
          setSlug(false);
        }
      })
      .catch(() => setSlug(false))
      .finally(() => setLoading(false));
  }, [hostname]);

  if (loading) return <LoadingScreen />;

  if (!slug) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center p-8">
          <p className="text-gray-500 text-lg mb-2">Sito non trovato</p>
          <p className="text-gray-400 text-sm">Questo dominio non è collegato a nessun sito su NoleggioinCloud.</p>
        </div>
      </div>
    );
  }

  // Dominio personalizzato verificato → mostra sito aziendale con routing pulito
  // Le route usano il virtual pathname con slug prefissato, ma la barra indirizzi
  // mostra l'URL pulito (es. tuonoleggio.it/contatti, non tuonoleggio.it/miazienda/contatti).
  return <CustomDomainSite slug={slug} />;
}

function CustomDomainSite({ slug }) {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u || null);
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, []);

  if (authLoading) return <LoadingScreen />;

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setUser(null);
    } catch (error) {
      console.error('Errore logout:', error);
    }
  };

  return (
    <BrowserRouter>
      <CompanyProvider user={user}>
        <CustomDomainRoutes slug={slug} user={user} setUser={setUser} onLogout={handleLogout} />
      </CompanyProvider>
    </BrowserRouter>
  );
}

// Separato da CustomDomainSite apposta: useLocation() funziona solo dentro
// <BrowserRouter>, e deve essere qui (non nel componente padre che monta il
// Router) perché è quello che rende il pathname virtuale REATTIVO alla
// navigazione lato client. Leggere window.location.pathname direttamente nel
// corpo di CustomDomainSite (come prima) calcolava il path solo al mount:
// cliccando un <Link> o chiamando navigate(), l'URL in barra cambiava ma
// questo componente non veniva ri-renderizzato (non essendo abbonato al
// router), quindi la pagina restava quella vecchia finché non si faceva
// refresh manuale.
// Piccolo helper: su dominio personalizzato, un utente già loggato che apre
// /{slug}/contratto/{bookingId} va reindirizzato al path "pulito"
// /contratto/{bookingId} (stessa eccezione already spiegata sopra per
// /account) — ma qui il redirect deve preservare il bookingId, quindi serve
// un componente che lo legga da useParams invece di un target statico.
function RedirectToCleanContractPath() {
  const { bookingId } = useParams();
  return <Navigate to={`/contratto/${bookingId}`} replace />;
}

function CustomDomainRoutes({ slug, user, setUser, onLogout }) {
  const location = useLocation();
  // Monto le route del sito pubblico con il pathname virtuale che include lo slug
  // così i componenti esistenti (useParams, Link) continuano a funzionare.
  // Difensivo: se il path reale contiene già lo slug come primo segmento
  // (link vecchi/salvati nei preferiti, da prima che i link interni fossero
  // "puliti" — vedi PublicCarList.js), non lo raddoppio.
  //
  // ECCEZIONE — "/account": deve restare un path REALE non prefissato (come
  // sul dominio di piattaforma) e renderizzare la dashboard cliente
  // direttamente qui sotto, invece di passare dalla variante con slug che
  // faceva `<Navigate to="/account" />`. Quel giro creava un loop infinito:
  // un utente già loggato su /account veniva reindirizzato a /account
  // (stesso path reale) → CustomDomainRoutes lo ri-prefissava di nuovo in
  // /slug/account → nuovo <Navigate> → di nuovo /account → ... pagina di
  // fatto irraggiungibile (loop di render, mai un risultato finale).
  const path = location.pathname; // es. "/" o "/contatti" (o, per link vecchi, "/dasdas/account")
  const alreadyHasSlug = path === `/${slug}` || path.startsWith(`/${slug}/`);
  const virtualPath = path === '/account'
    ? '/account'
    : path === '/'
      ? `/${slug}`
      : alreadyHasSlug ? path : `/${slug}${path}`;

  return (
    <Routes location={{ pathname: virtualPath, search: location.search, hash: location.hash }}>
      <Route path={`/${slug}/contatti`} element={<ContactPage companySlug={slug} />} />
      <Route path={`/${slug}/privacy`} element={<CompanyPrivacyPage companySlug={slug} />} />
      <Route path={`/${slug}/account`} element={
        user ? <Navigate to="/account" replace /> : (
          <Suspense fallback={<LoadingScreen />}><CustomerAuthScreen onAuth={setUser} /></Suspense>
        )
      } />
      <Route path="/account" element={
        user ? (
          <Suspense fallback={<LoadingScreen />}><UserDashboard user={user} onLogout={onLogout} /></Suspense>
        ) : (
          <Suspense fallback={<LoadingScreen />}><CustomerAuthScreen onAuth={setUser} /></Suspense>
        )
      } />
      {/* Firma anticipata del contratto — stesso pattern di /account: sullo
          stesso URL "pulito" (senza slug) mostra il gate di login se serve,
          la pagina di firma altrimenti — vedi ContractSignPage.js. */}
      <Route path={`/${slug}/contratto/:bookingId`} element={
        user ? <RedirectToCleanContractPath /> : (
          <Suspense fallback={<LoadingScreen />}><CustomerAuthScreen onAuth={setUser} /></Suspense>
        )
      } />
      <Route path="/contratto/:bookingId" element={
        user ? (
          <Suspense fallback={<LoadingScreen />}><ContractSignPage user={user} companySlug={slug} /></Suspense>
        ) : (
          <Suspense fallback={<LoadingScreen />}><CustomerAuthScreen onAuth={setUser} /></Suspense>
        )
      } />
      <Route path={`/${slug}`} element={<PublicCarList companySlug={slug} user={user} />} />
      <Route path="*" element={<PublicCarList companySlug={slug} user={user} />} />
    </Routes>
  );
}

function AppRoutes() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser || null);
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setUser(null);
    } catch (error) {
      console.error('Errore logout:', error);
    }
  };

  if (authLoading) return <LoadingScreen />;

  return (
    <CompanyProvider user={user}>
      <Routes>

        {/* ── Sito marketing ── */}
        <Route path="/"             element={<MarketingLayout><MarketingHome /></MarketingLayout>} />
        <Route path="/funzionalita" element={<MarketingLayout><MarketingFunz /></MarketingLayout>} />
        <Route path="/come-funziona"element={<MarketingLayout><MarketingCome /></MarketingLayout>} />
        <Route path="/prezzi"       element={<MarketingLayout><MarketingPrezzi /></MarketingLayout>} />
        <Route path="/tutorial"     element={<MarketingLayout><MarketingTutorial /></MarketingLayout>} />
        <Route path="/faq"          element={<MarketingLayout><MarketingFAQ /></MarketingLayout>} />
        <Route path="/contatti"     element={<MarketingLayout><MarketingContatti /></MarketingLayout>} />
        <Route path="/chi-siamo"    element={<MarketingLayout><MarketingChiSiamo /></MarketingLayout>} />
        <Route path="/quando-serve-un-gestionale-per-autonoleggio"  element={<MarketingLayout><GuideQuando /></MarketingLayout>} />
        <Route path="/quanto-costa-un-gestionale-per-autonoleggio" element={<MarketingLayout><GuideCosto /></MarketingLayout>} />
        <Route path="/come-scegliere-gestionale-autonoleggio"      element={<MarketingLayout><GuideScegliere /></MarketingLayout>} />
        <Route path="/gestionale-noleggio-moto"                    element={<MarketingLayout><GuideModo /></MarketingLayout>} />
        <Route path="/gestionale-noleggio-cargos-polizia"          element={<MarketingLayout><GuideCargos /></MarketingLayout>} />
        <Route path="/come-evitare-doppie-prenotazioni-autonoleggio" element={<MarketingLayout><GuideDoppie /></MarketingLayout>} />
        <Route path="/checklist-confronto-gestionali-autonoleggio" element={<MarketingLayout><GuideChecklist /></MarketingLayout>} />
        <Route path="/excel-whatsapp-o-gestionale-autonoleggio"    element={<MarketingLayout><GuideExcelVs /></MarketingLayout>} />
        <Route path="/cerca"        element={<MarketingLayout><SearchCompanies user={user} vehicleMode="tutti" /></MarketingLayout>} />
        <Route path="/noleggio-auto" element={<MarketingLayout><SearchCompanies user={user} vehicleMode="auto" /></MarketingLayout>} />
        <Route path="/noleggio-moto" element={<MarketingLayout><SearchCompanies user={user} vehicleMode="moto" /></MarketingLayout>} />
        <Route path="/noleggio-auto/:citta" element={<MarketingLayout><SearchCompanies user={user} vehicleMode="auto" /></MarketingLayout>} />
        <Route path="/noleggio-moto/:citta" element={<MarketingLayout><SearchCompanies user={user} vehicleMode="moto" /></MarketingLayout>} />

        {/* ── Broker OAuth per login Google sui domini personalizzati dei
            clienti (vedi AuthBrokerScreen.js). Esiste SOLO qui, dentro
            AppRoutes: questo garantisce che sia raggiungibile solo sui
            domini di piattaforma (PLATFORM_HOSTNAMES), cioè l'unico dominio
            autorizzato per l'OAuth Google — mai su un dominio cliente. */}
        <Route path="/auth-broker" element={
          <Suspense fallback={<LoadingScreen />}><AuthBrokerScreen /></Suspense>
        } />

        {/* ── App gestionale — accesso ── */}
        <Route
          path="/login"
          element={
            <PublicOnlyRoute user={user}>
              <Suspense fallback={<LoadingScreen />}><AuthScreen onAuth={setUser} /></Suspense>
            </PublicOnlyRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicOnlyRoute user={user}>
              <Suspense fallback={<LoadingScreen />}><RegisterScreen onAuth={setUser} user={user} /></Suspense>
            </PublicOnlyRoute>
          }
        />

        {/* ── App gestionale — aree protette ── */}
        {/* Rotta generica (nessuna azienda specifica): usata dal footer di /account.
            Il link company-specific è invece /:companySlug/privacy, più sotto. */}
        <Route
          path="/privacy"
          element={<PrivacyPage onBack={() => navigate(user ? '/account' : '/login')} />}
        />

        <Route
          path="/admin/*"
          element={
            <RequireAdmin user={user} onLogout={handleLogout}>
              <Suspense fallback={<LoadingScreen />}><AdminDashboard user={user} onLogout={handleLogout} /></Suspense>
            </RequireAdmin>
          }
        />

        <Route
          path="/platform-admin"
          element={
            <RequirePlatformAdmin user={user}>
              <Suspense fallback={<LoadingScreen />}><PlatformAdminPanel onLogout={handleLogout} /></Suspense>
            </RequirePlatformAdmin>
          }
        />

        <Route
          path="/account"
          element={
            user ? (
              <Suspense fallback={<LoadingScreen />}>
                <UserDashboard user={user} onLogout={handleLogout} />
              </Suspense>
            ) : (
              // Prima: <Navigate to="/login" /> mandava l'utente sulla schermata di
              // accesso per titolari di autonoleggio dopo il logout. Ora resta su
              // /account (nessun redirect) mostrando il login clienti dedicato.
              <Suspense fallback={<LoadingScreen />}><CustomerAuthScreen onAuth={setUser} /></Suspense>
            )
          }
        />

        {/* ── Siti pubblici aziende (slug dinamico) ──
            DEVONO stare DOPO tutte le rotte fisse per non intercettarle.
            Le parole riservate (login, register, admin, funzionalita, ecc.)
            sono già bloccate lato backend in registerCompany/checkSlugAvailability. */}
        <Route path="/:companySlug/contatti" element={<ContactPage />} />
        <Route path="/:companySlug/privacy" element={<CompanyPrivacyPage />} />
        {/* Firma anticipata del contratto — sempre con lo slug in URL (a
            differenza di /account, che è condiviso tra tutte le aziende con
            cui il cliente ha prenotato): questa pagina è per forza
            company-specific, serve a risolvere lo slug in companyId. */}
        <Route
          path="/:companySlug/contratto/:bookingId"
          element={
            user ? (
              <Suspense fallback={<LoadingScreen />}><ContractSignPage user={user} /></Suspense>
            ) : (
              <Suspense fallback={<LoadingScreen />}><CustomerAuthScreen onAuth={setUser} /></Suspense>
            )
          }
        />
        <Route
          path="/:companySlug/account"
          element={
            user ? (
              <Navigate to="/account" replace />
            ) : (
              <Suspense fallback={<LoadingScreen />}><CustomerAuthScreen onAuth={setUser} /></Suspense>
            )
          }
        />
        <Route path="/:companySlug"          element={<PublicCompanySite user={user} />} />

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </CompanyProvider>
  );
}

// ─── Banner globale "TEST MODE" ─────────────────────────────────────────────
// Visibile su OGNI pagina del sito (marketing, gestionale, sito pubblico
// aziende, checkout) finché il platform admin non disattiva il test mode dal
// suo pannello — non deve essere possibile ignorarlo per errore, perché
// mentre è attivo qualunque "pagamento" fatto sul sito è finto.
function TestModeBanner() {
  const { testMode } = useTestMode();
  if (!testMode) return null;
  return (
    <div className="sticky top-0 z-[100] flex items-center justify-center gap-2 px-4 py-2 bg-amber-400 text-amber-950 text-xs sm:text-sm font-black tracking-wide">
      <FlaskConical className="w-4 h-4 shrink-0" />
      MODALITÀ TEST ATTIVA — nessun pagamento reale viene elaborato
    </div>
  );
}

export default function App() {
  // Log accessi interno (Realtime Database, vedi functions/index.js:logAccess).
  // Nessun companyId: registra OGNI visita al dominio, chiunque sia e da
  // qualunque pagina arrivi — usato per le statistiche di traffico globali
  // nel pannello Platform Admin (non nel gestionale delle singole aziende).
  useEffect(() => {
    logAccess();
  }, []);

  return (
    <CustomDomainApp>
      <BrowserRouter>
        <ToastProvider>
          <ConfirmProvider>
            <TestModeBanner />
            <ErrorBoundary>
              <AppRoutes />
            </ErrorBoundary>
          </ConfirmProvider>
        </ToastProvider>
      </BrowserRouter>
    </CustomDomainApp>
  );
}
